import { ApiError, isMaintenanceError, normalizeApiError } from "@/lib/errors";
import { normalizeEnvelopeError, unwrapEnvelope } from "@/shared/api/envelope";
import { reportMaintenance } from "@/features/maintenance/maintenanceStore";

const DEFAULT_TIMEOUT_MS = 30_000;

export interface HttpClientOptions {
  baseUrl: string;
  getBearerToken?: () => string | null;
  onUnauthorized?: () => Promise<boolean>;
}

export interface HttpRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  auth?: boolean;
  bearer?: string | null;
  responseType?: "json" | "text" | "blob" | "empty";
  workspaceId?: string;
  idempotencyKey?: string;
  headers?: Record<string, string>;
  unwrapData?: boolean;
}

export interface HttpStreamOptions {
  signal?: AbortSignal;
  /** Bounds the wait for the response headers only; the stream itself runs until it ends or `signal` aborts. */
  timeoutMs?: number;
  workspaceId?: string;
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly getBearerToken?: () => string | null;
  private readonly onUnauthorized?: () => Promise<boolean>;

  constructor(options: HttpClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.getBearerToken = options.getBearerToken;
    this.onUnauthorized = options.onUnauthorized;
  }

  async request<T>(
    method: string,
    path: string,
    body: unknown | undefined,
    options: HttpRequestOptions = {},
  ): Promise<T> {
    const responseType = options.responseType ?? "json";
    const headers: Record<string, string> = {
      Accept:
        responseType === "text"
          ? "text/markdown"
          : responseType === "blob"
            ? "application/octet-stream"
            : "application/json",
    };

    if (body !== undefined) headers["Content-Type"] = "application/json";
    const bearer =
      options.bearer ?? (options.auth === false ? null : this.getBearerToken?.() ?? null);
    if (bearer) headers.Authorization = `Bearer ${bearer}`;
    if (options.workspaceId) headers["X-Workspace-ID"] = options.workspaceId;
    if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
    Object.assign(headers, options.headers || {});

    const execute = async (): Promise<T> => {
      const controller = new AbortController();
      const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
      const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
      const onAbort = () => controller.abort();
      options.signal?.addEventListener("abort", onAbort);

      try {
        const response = await fetch(`${this.baseUrl}${path}`, {
          method,
          headers,
          body: body === undefined ? undefined : JSON.stringify(body),
          credentials: "include",
          signal: controller.signal,
        });

        if (responseType === "empty") {
          if (!response.ok) {
            const rawText = await response.text();
            throw parseFailedResponse(response.status, rawText);
          }
          return undefined as T;
        }

        if (responseType === "blob") {
          if (!response.ok) {
            const rawText = await response.text();
            throw parseFailedResponse(response.status, rawText);
          }
          return (await response.blob()) as T;
        }

        const rawText = await response.text();
        let parsed: unknown = null;
        if (responseType === "text") {
          parsed = rawText;
        } else if (rawText) {
          try {
            parsed = JSON.parse(rawText);
          } catch {
            parsed = { error: rawText };
          }
        }

        if (!response.ok) throw parseFailedResponse(response.status, parsed);
        if (options.unwrapData) return unwrapEnvelope<T>(parsed);
        return parsed as T;
      } finally {
        window.clearTimeout(timeoutId);
        options.signal?.removeEventListener("abort", onAbort);
      }
    };

    try {
      return await execute();
    } catch (error) {
      noteMaintenance(method, error);
      if (
        error instanceof ApiError &&
        error.status === 401 &&
        options.auth !== false &&
        this.onUnauthorized
      ) {
        const recovered = await this.onUnauthorized();
        if (recovered) {
          try {
            return await execute();
          } catch (retryError) {
            // Maintenance may have started between the two attempts.
            noteMaintenance(method, retryError);
            throw retryError;
          }
        }
      }
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new Error("Request timed out.", { cause: error });
      }
      throw error;
    }
  }

  /**
   * Opens a server-sent-events response. It resolves once the headers say the
   * stream has started; a failure before that (validation, limits, access) is an
   * ordinary JSON error and throws the same ApiError as `request`, including the
   * one-time session recovery on 401. After that only `options.signal` ends the
   * stream, so a long answer is never cut by the header timeout.
   */
  async openStream(method: string, path: string, body: unknown | undefined, options: HttpStreamOptions = {}): Promise<Response> {
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const bearer = this.getBearerToken?.() ?? null;
    if (bearer) headers.Authorization = `Bearer ${bearer}`;
    if (options.workspaceId) headers["X-Workspace-ID"] = options.workspaceId;

    const open = async (): Promise<Response> => {
      const controller = new AbortController();
      let timedOut = false;
      const timer = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
      const forwardAbort = () => controller.abort();
      if (options.signal?.aborted) controller.abort();
      else options.signal?.addEventListener("abort", forwardAbort, { once: true });
      try {
        const response = await fetch(`${this.baseUrl}${path}`, {
          method,
          headers,
          body: body === undefined ? undefined : JSON.stringify(body),
          credentials: "include",
          signal: controller.signal,
        });
        if (!response.ok) throw parseFailedResponse(response.status, parseBody(await response.text()));
        if (!response.body || !(response.headers.get("content-type") ?? "").includes("text/event-stream")) {
          throw new Error("The server did not start an answer stream.");
        }
        // forwardAbort stays registered: aborting the caller's signal now ends the stream.
        return response;
      } catch (error) {
        options.signal?.removeEventListener("abort", forwardAbort);
        if (error instanceof DOMException && error.name === "AbortError" && timedOut && !options.signal?.aborted) {
          throw new Error("Request timed out.", { cause: error });
        }
        throw error;
      } finally {
        window.clearTimeout(timer);
      }
    };

    try {
      return await open();
    } catch (error) {
      noteMaintenance(method, error);
      if (error instanceof ApiError && error.status === 401 && this.onUnauthorized && (await this.onUnauthorized())) {
        try {
          return await open();
        } catch (retryError) {
          noteMaintenance(method, retryError);
          throw retryError;
        }
      }
      throw error;
    }
  }
}

/**
 * A 503 MAINTENANCE answer is a decision by an admin, not a failure of the
 * session: tell the app so it can show the maintenance page, and let the error
 * carry on to the caller unchanged.
 */
function noteMaintenance(method: string, error: unknown): void {
  if (isMaintenanceError(error)) {
    reportMaintenance(method, error.body.details);
  }
}

function parseBody(rawText: string): unknown {
  if (!rawText) return null;
  try {
    return JSON.parse(rawText);
  } catch {
    return { error: rawText };
  }
}

function parseFailedResponse(status: number, parsed: unknown): ApiError {
  if (
    parsed &&
    typeof parsed === "object" &&
    ("meta" in parsed || (typeof (parsed as { error?: unknown }).error === "object"))
  ) {
    return normalizeEnvelopeError(status, parsed);
  }
  return normalizeApiError(status, parsed);
}

export function resolveApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (typeof fromEnv === "string" && fromEnv.length > 0) return fromEnv.replace(/\/$/, "");
  return import.meta.env.DEV ? "/api" : "https://api.memoru.id";
}
